import mermaid from 'mermaid';
import type { MermaidErrorInfo, Theme } from '../store/types.ts';

export function getMermaidConfig(theme: Theme) {
  return {
    startOnLoad: false,
    securityLevel: 'loose' as const,
    theme: (theme === 'dark' ? 'dark' : 'default') as 'dark' | 'default',
    fontFamily: 'Inter, sans-serif',
    flowchart: { htmlLabels: true, curve: 'basis' as const, nodeSpacing: 30, rankSpacing: 50, padding: 15 },
    fontSize: 16,
    themeVariables:
      theme === 'dark'
        ? {
            background: '#111113',
            primaryColor: '#00D4AA',
            primaryTextColor: '#EDEDEF',
            lineColor: '#5C5C63',
          }
        : {
            background: '#ffffff',
            primaryColor: '#00D4AA',
            primaryTextColor: '#1A1A1E',
            lineColor: '#8B8B8F',
          },
  };
}

export function parseMermaidError(raw: string): MermaidErrorInfo {
  const info: MermaidErrorInfo = { raw };
  const lineMatch = raw.match(/line\s+(\d+)/i) || raw.match(/on\s+line\s+(\d+)/i);
  const colMatch = raw.match(/col(?:umn)?\s*(\d+)/i);
  if (lineMatch?.[1]) info.line = Number(lineMatch[1]);
  if (colMatch?.[1]) info.column = Number(colMatch[1]);
  return info;
}

export function cleanMermaidCode(text: string): string {
  const fenceMatch = text.match(/```(?:mermaid|[a-zA-Z]*)\s*([\s\S]*?)```/i);
  if (fenceMatch) return fenceMatch[1].trim();
  return text.replace(/^```[a-zA-Z]*\s*/i, '').replace(/\s*```$/, '').trim();
}

let lastTheme: Theme | null = null;

export async function renderMermaidToSvg(
  code: string,
  theme: Theme,
): Promise<{ svg?: string; error?: MermaidErrorInfo }> {
  try {
    if (lastTheme !== theme) {
      mermaid.initialize(getMermaidConfig(theme));
      lastTheme = theme;
    }
    const id = `mmd-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const { svg } = await mermaid.render(id, code);
    document.getElementById(id)?.remove();
    return { svg };
  } catch (error) {
    const raw = error instanceof Error ? error.message : String(error);
    return { error: parseMermaidError(raw) };
  }
}
